import { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { User, Settings, LogOut, Crown, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Profile } from "@/types/auth";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { differenceInDays, parseISO } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

type UserProfileDropdownProps = {
  userId: string | undefined;
  profile: Profile | null;
  avatarUrl: string;
};

export const UserProfileDropdown = ({ userId, profile, avatarUrl }: UserProfileDropdownProps) => {
  const navigate = useNavigate();
  const { t } = useTranslations("dashboard");
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (profile?.subscription_plan === 'trial' && profile.subscription_end_date) {
      const endDate = parseISO(profile.subscription_end_date);
      const today = new Date();
      const days = differenceInDays(endDate, today);
      setDaysRemaining(days >= 0 ? days : 0);
    } else {
      setDaysRemaining(null);
    }
  }, [profile]);

  const getPlanName = () => {
    switch (profile?.subscription_plan) {
      case 'pro':
        return t("header.planNames.pro");
      case 'premium':
        return t("header.planNames.premium");
      case 'trial':
        return t("header.planNames.trial");
      case 'free':
      default:
        return t("header.planNames.free");
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      try {
        const { error } = await supabase.auth.signOut();
        if (error && error.status !== 403) {
          console.error("Non-403 error during signOut:", error);
        }
        
        toast.success(language === "pt" ? "Sessão terminada com sucesso" : "Successfully signed out", {
          position: "bottom-center"
        });
      } catch (signOutError) {
        console.error("Error during signOut:", signOutError);
      }
      navigate(getPath("/"));
    } catch (error) {
      console.error("Error during logout:", error);
      localStorage.clear();
      sessionStorage.clear();
      navigate(getPath("/"));
    }
  };

  const handleUpgradeClick = () => {
    navigate(getPath('/dashboard/plan'));
  };

  return (
    <div className="flex flex-col items-end relative">
      <DropdownMenu>
        <DropdownMenuTrigger className="focus:outline-none">
          <div className="flex items-center gap-2 p-2 rounded-full hover:bg-slate-gray-light/10 dark:hover:bg-gray-700/30 transition-colors">
            <Avatar className="h-8 w-8 ring-2 ring-flame/10">
              <AvatarImage src={avatarUrl} />
              <AvatarFallback className="bg-flame/10 text-flame">
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:block text-sm text-right">
              <p className="font-medium text-black dark:text-white">
                {userId ? userId.split('@')[0] : ''}
              </p>
              <div className="flex items-center text-xs text-slate-gray-light dark:text-gray-400 gap-1">
                <Crown className="h-3 w-3 text-flame" />
                {getPlanName()}
              </div>
            </div>
            {!isMobile && daysRemaining !== null && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div 
                      className="ml-1 text-xs bg-flame/10 text-flame px-2 py-0.5 rounded-full flex items-center gap-1 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpgradeClick();
                      }}
                    >
                      <Calendar className="h-3 w-3" />
                      <span>{daysRemaining}/14</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="bg-gray-900 text-white border-gray-800">
                    <p>{language === 'pt' ? `${daysRemaining} dias restantes no período de avaliação` : `${daysRemaining} days remaining in trial period`}</p>
                    <Button 
                      size="sm" 
                      variant="link" 
                      className="text-flame p-0 h-6 mt-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpgradeClick();
                      }}
                    >
                      {language === 'pt' ? 'Fazer Upgrade' : 'Upgrade Plan'}
                    </Button>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-56 p-2 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md border border-slate-gray-light/10 dark:border-gray-700/50 shadow-ios rounded-xl z-[200]"
        >
          {daysRemaining !== null && (
            <div className="p-2 mb-2 bg-flame/10 rounded-md flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-flame" />
                <span className="text-sm text-flame font-medium">
                  {language === 'pt' 
                    ? `${daysRemaining} dias restantes` 
                    : `${daysRemaining} days remaining`}
                </span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 py-0 px-2 border-flame text-flame hover:bg-flame/10"
                onClick={handleUpgradeClick}
              >
                {language === 'pt' ? 'Upgrade' : 'Upgrade'}
              </Button>
            </div>
          )}
          
          <DropdownMenuItem onClick={() => navigate(getPath('/dashboard/settings'))} className="p-2 rounded-md text-slate-gray-dark dark:text-gray-300 hover:text-black dark:hover:text-white hover:bg-slate-gray-light/10 dark:hover:bg-gray-700/50">
            <Settings className="mr-2 h-4 w-4" />
            <span>{t("header.userMenu.settings")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout} className="p-2 rounded-md text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20">
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t("header.userMenu.signOut")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      {isMobile && daysRemaining !== null && (
        <div 
          className="absolute top-16 left-0 mt-1 text-xs bg-flame/10 text-flame px-2 py-1 rounded-full flex items-center gap-1 cursor-pointer z-[150]"
          onClick={handleUpgradeClick}
        >
          <Calendar className="h-3 w-3" />
          <span>{daysRemaining}/14</span>
        </div>
      )}
    </div>
  );
};
